// src/utils/formatNumbers.ts

import i18n from '../i18n'

/**
 * Resolve a valid locale string from input or browser settings
 */
export const resolveLocale = (lang?: string): string => {
  if (!lang) {
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language
    }
    return 'en-US'
  }

  const code = lang.toLowerCase().split('-')[0]
  if (code === 'en') return 'en-US'
  if (code === 'fr') return 'fr-FR'

  return lang // fallback: use provided value
}

/** Locale used for number separators: follows the UI language. */
export const numberLocale = (lang?: string): string =>
  resolveLocale(lang ?? i18n.language)

const SPACES = /[\u00A0\u202F]/g

/** API values (number or "1500.000") or user text → number. */
export const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n =
    typeof value === 'number'
      ? value
      : /^\s*-?\d+(\.\d+)?\s*$/.test(String(value))
        ? Number(value)
        : parseLocaleNumber(String(value))
  return n === null || !Number.isFinite(n) ? null : n
}

/**
 * Generic number formatter
 */
export const formatNumber = (
  value: number,
  {
    locale,
    decimals = 0,
    useGrouping = true,
    replaceNbspWithSpace = true,
  }: {
    locale?: string
    decimals?: number
    useGrouping?: boolean
    replaceNbspWithSpace?: boolean
  } = {}
): string => {
  const resolvedLocale = resolveLocale(locale)

  const formatter = new Intl.NumberFormat(resolvedLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping,
  })

  let formatted = formatter.format(value)

  if (replaceNbspWithSpace) {
    formatted = formatted.replace(SPACES, ' ')
  }

  return formatted
}

type FormatOptions = { locale?: string; decimals?: number }

/** 2 000 000,50 (fr) / 2,000,000.50 (en). Returns '—' for empty values. */
export const formatDecimal = (
  value: unknown,
  { locale, decimals = 2 }: FormatOptions = {}
): string => {
  const n = toNumber(value)
  if (n === null) return '—'
  return formatNumber(n, { locale: numberLocale(locale), decimals })
}

/** Money with the organisation currency: 2 000 000,00 € / €2,000,000.00 */
export const formatMoney = (
  value: unknown,
  currencyCode?: string,
  { locale, decimals = 2 }: FormatOptions = {}
): string => {
  const n = toNumber(value)
  if (n === null) return '—'
  const resolvedLocale = numberLocale(locale)
  if (currencyCode) {
    try {
      return new Intl.NumberFormat(resolvedLocale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
        .format(n)
        .replace(SPACES, ' ')
    } catch {
      /* unknown ISO code: fall back to "1 000,00 XYZ" */
    }
  }
  const amount = formatNumber(n, { locale: resolvedLocale, decimals })
  return currencyCode ? `${amount} ${currencyCode}` : amount
}

/** Value already expressed in percent (25 → "25,00 %" / "25.00%"). */
export const formatPercent = (
  value: unknown,
  { locale, decimals = 2 }: FormatOptions = {}
): string => {
  const n = toNumber(value)
  if (n === null) return '—'
  return new Intl.NumberFormat(numberLocale(locale), {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
    .format(n / 100)
    .replace(SPACES, ' ')
}

/** Currency symbol for an ISO code in the given locale (EUR → €). */
export const currencySymbol = (currencyCode?: string, locale?: string): string => {
  if (!currencyCode) return ''
  try {
    const part = new Intl.NumberFormat(numberLocale(locale), {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency')
    return part?.value || currencyCode
  } catch {
    return currencyCode
  }
}

/** Decimal separator of a locale ("," for fr-FR, "." for en-US). */
export const decimalSeparator = (locale?: string): string =>
  new Intl.NumberFormat(numberLocale(locale))
    .formatToParts(1.1)
    .find((p) => p.type === 'decimal')?.value || '.'

/**
 * Parse user text into a number. Accepts grouping spaces/commas/dots and
 * either decimal separator ("2 000 000,5", "2,000,000.5", "25.5").
 */
export function parseLocaleNumber(text: string, locale?: string): number | null {
  let s = String(text ?? '')
    .trim()
    .replace(/[\s\u00A0\u202F'’]/g, '')
  if (!s) return null
  const negative = s.startsWith('-')
  s = s.replace(/^[-+]/, '')
  if (!/^[\d.,]+$/.test(s)) return null

  const lastDot = s.lastIndexOf('.')
  const lastComma = s.lastIndexOf(',')
  let decimalIndex = -1

  if (lastDot >= 0 && lastComma >= 0) {
    decimalIndex = Math.max(lastDot, lastComma)
  } else if (lastDot >= 0 || lastComma >= 0) {
    const sep = lastDot >= 0 ? '.' : ','
    const index = Math.max(lastDot, lastComma)
    const occurrences = s.split(sep).length - 1
    const digitsAfter = s.length - index - 1
    const isLocaleDecimal = sep === decimalSeparator(locale)
    if (occurrences === 1 && (isLocaleDecimal || digitsAfter !== 3)) {
      decimalIndex = index
    }
  }

  const intPart = (decimalIndex >= 0 ? s.slice(0, decimalIndex) : s).replace(/[.,]/g, '')
  const fracPart = decimalIndex >= 0 ? s.slice(decimalIndex + 1).replace(/[.,]/g, '') : ''
  const n = Number(`${intPart || '0'}${fracPart ? `.${fracPart}` : ''}`)
  if (!Number.isFinite(n)) return null
  return negative ? -n : n
}

/** Round to a fixed number of decimals without float noise (25.567 → 25.57). */
export const roundTo = (value: number, decimals = 2): number => {
  const f = 10 ** decimals
  return Math.round((value + Number.EPSILON) * f) / f
}

/**
 * Currency formatter (legacy signature, kept for existing call sites).
 */
export const locale = i18n.language
export const formatCurrency = (
  value: number,
  currencyCode?: string,
  opts?: {
    locale?: string
    decimals?: number
    useGrouping?: boolean
    replaceNbspWithSpace?: boolean
    useIntl?: boolean
  }
): string => formatMoney(value, currencyCode, { decimals: opts?.decimals ?? 2 })
